import React, { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { UserClient } from '../api/api';
import useApiClient from '../common/hooks/useApiClient';
import AuthContext from './AuthContext';

export interface CurrentUserPermissions {
	permissions: readonly string[];
	isLoading: boolean;
	hasPermission: (permissionId: string) => boolean;
	refresh: () => Promise<void>;
}

export const CurrentUserPermissionsContext = createContext<CurrentUserPermissions>({
	permissions: [],
	isLoading: false,
	hasPermission: () => false,
	refresh: async () => undefined,
});

export function CurrentUserPermissionsProvider({ children }: PropsWithChildren) {
	const auth = useContext(AuthContext);
	const userClient = useApiClient(UserClient);
	const accountKey = auth.account?.homeAccountId ?? auth.account?.localAccountId ?? null;
	const [permissions, setPermissions] = useState<readonly string[]>([]);
	const [loadState, setLoadState] = useState({ accountKey: null as string | null, isPending: false, isLoaded: false });
	const requestVersion = useRef(0);
	const invalidateRequests = useCallback(() => {
		requestVersion.current++;
	}, []);

	const loadPermissions = useCallback(async () => {
		const request = ++requestVersion.current;
		if (!auth.isAuthenticated) {
			setPermissions([]);
			setLoadState({ accountKey, isPending: false, isLoaded: true });
			return;
		}

		setLoadState({ accountKey, isPending: true, isLoaded: false });
		try {
			const user = await userClient.getUser(undefined);
			if (request === requestVersion.current) {
				setPermissions(user.permissions ?? []);
				setLoadState({ accountKey, isPending: false, isLoaded: true });
			}
		} catch (error) {
			if (request === requestVersion.current) {
				console.error('Failed to load current user permissions:', error);
				setPermissions([]);
				setLoadState({ accountKey, isPending: false, isLoaded: true });
			}
		}
	}, [accountKey, auth.isAuthenticated, userClient]);

	useEffect(() => {
		void loadPermissions();
		return invalidateRequests;
	}, [invalidateRequests, loadPermissions]);

	const hasPermission = useCallback((permissionId: string) => permissions.includes(permissionId), [permissions]);
	const isLoading = auth.isAuthenticated && (loadState.accountKey !== accountKey || loadState.isPending || !loadState.isLoaded);
	const refresh = loadPermissions;
	const value = useMemo(() => ({ permissions, isLoading, hasPermission, refresh }), [permissions, isLoading, hasPermission, refresh]);

	return <CurrentUserPermissionsContext.Provider value={value}>{children}</CurrentUserPermissionsContext.Provider>;
}

export function useCurrentUserPermissions() {
	return useContext(CurrentUserPermissionsContext);
}
